import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "./Components/Header";
import { useLocation, useNavigate } from "react-router-dom";
import PageContainer from "./Components/PageContainer";
import MainContent from "./Components/MainContent";
import SectionTitle from "./Components/SectionTitle";
import TutorCard from "./Components/TutorCard";
import {
  getTeacherDisplayName,
  getTeacherIdentifier,
  getTeacherPhotoValue,
  normalizeAvailabilityCollection,
  normalizeTeacherCollection,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  normalizeAudPriceForDisplay,
  normalizeAudPriceToMinorUnits,
} from "../../utils/audCurrency";
import { resolveApiImageUrl } from "../../utils/imageUrl";
import { formatTeacherYearLabels } from "../../utils/teacherYears";
import { getClassBookingQueryErrorMessage } from "../../utils/uiErrorMessages";
import { getClassBookingBasePath } from "../../features/parentFlow/parentFlow";
import { mergePendingTutorSlotsFromStorage } from "./pendingTutorAvailability";

const buildAvatarDataUri = (name = "Teacher") => {
  const safeName = String(name || "Teacher").trim() || "Teacher";
  const initial = safeName.charAt(0).toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f97316" />
          <stop offset="100%" stop-color="#ef4444" />
        </linearGradient>
      </defs>
      <rect width="128" height="128" rx="24" fill="url(#bg)" />
      <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="48" font-weight="700" fill="white">
        ${initial}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const parseSlotDate = (value) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isFutureAvailabilitySlot = (slot, now = new Date()) => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  if (!startDate) return false;
  return startDate.getTime() > now.getTime();
};

const buildTutorCardModel = (teacher, index) => {
  const teacherName = getTeacherDisplayName(teacher);
  const teacherId = getTeacherIdentifier(teacher);
  const years = formatTeacherYearLabels(
    teacher?.year_labels?.length ? teacher.year_labels : teacher?.year
  ).slice(0, 4);
  const rawPricePerSession = teacher?.price_per_session ?? teacher?.price;
  const pricePerSession = normalizeAudPriceForDisplay(rawPricePerSession);
  const rating = toNumber(teacher?.rating);
  const reviewCount = toNumber(teacher?.reviews ?? teacher?.review_count ?? teacher?.total_reviews);
  const experience = toNumber(teacher?.experience);
  const description = [
    teacher?.specializations,
    teacher?.qualifications,
    teacher?.subjects,
    teacher?.bio,
  ].find((value) => String(value || "").trim());

  return {
    id: teacherId || `teacher-${index}`,
    routeId: teacherId || null,
    name: teacherName,
    image: resolveApiImageUrl(getTeacherPhotoValue(teacher)) || buildAvatarDataUri(teacherName),
    rating: rating ?? "New",
    reviews:
      reviewCount !== null
        ? `${reviewCount} review${reviewCount === 1 ? "" : "s"}`
        : "Profile available",
    experience:
      experience !== null
        ? `${experience} year${experience === 1 ? "" : "s"} exp`
        : "Experience not provided",
    description:
      String(description || "").trim() ||
      "Teacher profile is available for booking.",
    years,
    price: pricePerSession,
    priceMinorUnits: normalizeAudPriceToMinorUnits(rawPricePerSession),
  };
};

const BookTutor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bookingBasePath = getClassBookingBasePath(location.pathname);
  const isParentFlow = bookingBasePath.startsWith("/parent/");

  const tutorListQuery = useQuery({
    queryKey: ["book-tutors"],
    queryFn: async () => {
      const tutorPayload = await teacherApi.getTutors();
      const tutors = normalizeTeacherCollection(tutorPayload).filter((teacher) =>
        Boolean(getTeacherIdentifier(teacher))
      );

      const availabilityResults = await Promise.allSettled(
        tutors.map((teacher) =>
          teacherApi.getTutorAvailability(getTeacherIdentifier(teacher))
        )
      );

      return tutors.filter((teacher, index) => {
        const tutorId = getTeacherIdentifier(teacher);
        const availabilityResult = availabilityResults[index];
        const availableSlots = mergePendingTutorSlotsFromStorage({
          tutorId,
          availabilitySlots:
            availabilityResult?.status === "fulfilled"
              ? normalizeAvailabilityCollection(availabilityResult.value)
              : [],
        }).filter((slot) => isFutureAvailabilitySlot(slot));

        return availableSlots.length > 0;
      });
    },
    retry: false,
  });

  const tutors = useMemo(
    () =>
      normalizeTeacherCollection(tutorListQuery.data).map((teacher, index) =>
        buildTutorCardModel(teacher, index)
      ),
    [tutorListQuery.data]
  );

  const filteredTutors = useMemo(() => {
    return tutors;
  }, [tutors]);

  const handleTutorClick = (tutorId) => {
    if (!tutorId) return;
    navigate(`${bookingBasePath}/tutorDetails/${tutorId}`);
  };

  return (
    <PageContainer variant={isParentFlow ? "parent" : "default"}>
      <div
        className={`sticky top-0 z-50 ${
          isParentFlow
            ? "bg-[#f8f3ea]/95"
            : "bg-[#DFF2EB]/70"
        } backdrop-blur-md`}
      >
        <Header parentLogoutTone={isParentFlow ? "violet" : "orange"} />
      </div>

      <MainContent variant={isParentFlow ? "parent" : "default"}>
        <SectionTitle 
          title={isParentFlow ? "Choose a Tutor for Parent Booking" : "Find Your Perfect Tutor"} 
          subtitle={
            isParentFlow
              ? "Choose a tutor and book a session for your child in one simple place."
              : ""
          }
          titleClassName={isParentFlow ? "text-[#f97316]" : ""}
        />

        <p
          className={`mt-6 font-medium ${
            isParentFlow ? "text-[#0B2B66]" : "text-gray-700"
          }`}
        >
          Showing {filteredTutors.length} tutors
        </p>

        {tutorListQuery.isLoading ? (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600 shadow-sm">
            Loading tutors...
          </div>
        ) : tutorListQuery.isError ? (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-red-700 shadow-sm">
            {getClassBookingQueryErrorMessage(tutorListQuery.error, "tutors")}
          </div>
        ) : !filteredTutors.length ? (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600 shadow-sm">
            No tutors found for your current search.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-6 mt-6">
            {filteredTutors.map((tutor, index) => (
              <TutorCard
                key={tutor.id}
                tutor={tutor}
                onClick={() => handleTutorClick(tutor.routeId)}
              />
            ))}
          </div>
        )}
      </MainContent>
    </PageContainer>
  );
};

export default BookTutor;
